
import { Component, Vue, Watch } from 'vue-property-decorator'
import { namespace } from 'vuex-class'
import {
  XlsxRead,
  XlsxSheets,
  XlsxJson,
  XlsxWorkbook,
  XlsxSheet,
  XlsxDownload
} from 'vue-xlsx'
import FileReader from 'vue-filereader'
import {
  uploaMassivePatientsFile,
  syncLoadedPatients,
  getPatientsLoaded // 👈 IMPORTA TU MÉTODO
} from '@/components/patients/patients.service'
import { PaginationVM } from '@/models'
import moment from 'moment'

const AppModule = namespace('app')

@Component({
  components: {
    XlsxRead,
    XlsxSheets,
    XlsxJson,
    XlsxWorkbook,
    XlsxSheet,
    XlsxDownload,
    FileReader
  }
})
export default class UploadMassivePatientView extends Vue {
  @AppModule.Getter
  public _isUploadCompleted!: () => boolean

  @AppModule.Action('setUploadCompletedFlag')
  public setUploadCompletedFlag!: (flag: boolean) => Promise<void>
  public expandAllRows = false
  public fields = [
    { key: 'uploadIdentifier', label: 'Upload Identifier' },
    { key: 'npi', label: 'Npi' },
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'sex', label: 'Sex' },
    { key: 'dob', label: 'Dob', formatter: 'formatDate' },
    { key: 'insuranceName', label: 'Insurance Name' },
    { key: 'insurancePlan', label: 'Insurance Plan' },
    { key: 'phone', label: 'Phone' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'email1', label: 'Email1' },
    { key: 'street', label: 'Street' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'zip', label: 'Zip' },
    { key: 'insuranceContractNo', label: 'Insurance Contract No' },
    { key: 'uploadMessageStatus', label: 'Upload Message Status' },
    { key: 'uploadDate', label: 'Upload Date', formatter: 'formatDate' }
  ]
  public items = []
  public file: File | null = null
  public patientsLoaded = [] // 👈 NUEVO
  public perPage = 10
  public pageOptions = [10, 20, 50, 100, 250, 500, 1000]
  public pagination = {} as PaginationVM
  selectedSheet: string | null = null
  sheetName = null
  public uploadIdToSync = ''
  public isLoadingAfterUpload = false
  public showDownloadBtn = false
  private uploadKeepAlive: number | null = null

  @Watch('perPage')
  perPageChange() {
    this.getPatientsLoaded(this.pagination.currentPage)
  }

  @Watch('_isUploadCompleted')
  async onUploadCompleted(val: boolean) {
    if (val && this.uploadIdToSync) {
      this.isLoadingAfterUpload = true
      try {
        await this.getPatientsLoaded(1)
        await this.syncData()
      } finally {
        this.showDownloadBtn = true
      }
    }
  }

  get _pagination() {
    return this.pagination
  }

  public async onPageChange(pageNumber: number) {
    // this.expandAllRows = false
    // console.log(pageNumber)
    await this.getPatientsLoaded(pageNumber)
  }

  public onParse(e: string[]) {
    this.selectedSheet = e[0]
  }

  public loadFile(e: File) {
    this.file = e
  }

  beforeDestroy() {
    this.stopUploadSession()
  }

  private startUploadSession() {
    this.$store.commit('app/setPatientUploadInProgress', true)
    this.touchActivity()
    this.uploadKeepAlive = window.setInterval(() => this.touchActivity(), 60000)
  }

  private stopUploadSession() {
    if (this.uploadKeepAlive != null) {
      window.clearInterval(this.uploadKeepAlive)
      this.uploadKeepAlive = null
    }
    this.touchActivity()
    this.$store.commit('app/setPatientUploadInProgress', false)
  }

  private touchActivity() {
    window.dispatchEvent(new Event('mousemove'))
  }

  public async upload() {
    if (!this.file) return

    const formData = new FormData()
    formData.append('file', this.file)

    this.startUploadSession()
    try {
      const response = await uploaMassivePatientsFile(formData)

      if (response.success) {
        this.uploadIdToSync = response.batchId

        await this.getPatientsLoaded(1)

        this.$bvToast.toast(
          `Action submitted successfully. \n ${response.message}`,
          {
            title: 'Success',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 5000,
            appendToast: true
          }
        )
      } else {
        this.$bvToast.toast(`An error has occur. \n ${response.message}`, {
          title: 'Warning',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 5000,
          appendToast: true
        })
      }
    } finally {
      this.stopUploadSession()
    }
  }
  public formatDate(value: string) {
    if (value) return moment(value).format('MM/DD/YYYY')
  }
  public async getPatientsLoaded(pageNumber) {
    if (!this.uploadIdToSync) return

    const response = await getPatientsLoaded(
      pageNumber,
      this.perPage,
      this.uploadIdToSync
    )
    this.patientsLoaded = response?.data || []
    this.pagination = JSON.parse(response.headers['x-pagination'])
  }

  public async syncData() {
    if (!this.uploadIdToSync) return

    try {
      // Fetch all data without pagination
      const response = await getPatientsLoaded(1, 99999, this.uploadIdToSync)
      const allData = response?.data || []

      if (allData.length === 0) {
        this.$bvToast.toast('No data available to export', {
          title: 'Info',
          variant: 'info',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          autoHideDelay: 3000
        })
        return
      }

      // Export to Excel
      this.exportToExcel(allData)

      // Reset the flag after successful export
      await this.setUploadCompletedFlag(false)

      this.$bvToast.toast('Data exported successfully', {
        title: 'Success',
        variant: 'success',
        solid: true,
        toaster: 'b-toaster-bottom-right',
        autoHideDelay: 3000
      })
    } catch (error) {
      console.error('Error during sync:', error)
      this.$bvToast.toast('Error exporting data. Please try again.', {
        title: 'Error',
        variant: 'danger',
        solid: true,
        toaster: 'b-toaster-bottom-right',
        autoHideDelay: 3000
      })
    }
  }

  public exportToExcel(data: any[]) {
    // Define column headers
    const headers = [
      'Upload Identifier',
      'NPI',
      'First Name',
      'Last Name',
      'Sex',
      'DOB',
      'Insurance Name',
      'Insurance Plan',
      'Phone',
      'Mobile',
      'Email',
      'Street',
      'City',
      'State',
      'Zip',
      'Insurance Contract No',
      'Upload Message Status',
      'Upload Date'
    ]

    // Convert data to worksheet format
    const worksheetData = data.map(row => [
      row.uploadIdentifier,
      row.npi,
      row.firstName,
      row.lastName,
      row.sex,
      row.dob ? moment(row.dob).format('MM/DD/YYYY') : '',
      row.insuranceName,
      row.insurancePlan,
      row.phone,
      row.mobile,
      row.email1,
      row.street,
      row.city,
      row.state,
      row.zip,
      row.insuranceContractNo,
      row.uploadMessageStatus,
      row.uploadDate ? moment(row.uploadDate).format('MM/DD/YYYY') : ''
    ])

    // Create worksheet with headers and data
    const worksheet = [headers, ...worksheetData]

    // Create a Blob from the worksheet data
    const csv = worksheet
      .map(row =>
        row
          .map(cell => {
            if (cell === null || cell === undefined) return ''
            const cellStr = String(cell).replace(/"/g, '""')
            return cellStr.includes(',') ? `"${cellStr}"` : cellStr
          })
          .join(',')
      )
      .join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })

    // Create download link
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute(
      'download',
      `loaded-patients-${moment().format('YYYY-MM-DD-HHmmss')}.csv`
    )
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}
